import React from 'react';
import { Box } from '@mui/material';

/**
 * Small animated icons for the three "Where do you want to start?" cards.
 *
 * These are inline SVG with CSS keyframes rather than animated GIFs, which is
 * what the brief asked for in spirit but not in format. A GIF would be 100–300 kB
 * each, baked at one colour and one size, and — the part that matters most — it
 * cannot be stopped, so it ignores `prefers-reduced-motion` entirely. Everything
 * here is a few hundred bytes, inherits `currentColor`, stays crisp at any size,
 * and freezes when the visitor has asked for stillness.
 *
 * The motion is deliberately slow and small. These sit beside the page's primary
 * call to action, and anything livelier would compete with it.
 */

const reduceMotion = {
  '@media (prefers-reduced-motion: reduce)': {
    '& *': { animation: 'none !important' },
  },
};

const frame = (size) => ({
  width: size,
  height: size,
  display: 'block',
  overflow: 'visible',
});

/** "I'm new" — a compass needle settling, as if getting its bearings. */
export const CompassIcon = ({ size = 30 }) => (
  <Box
    component="svg"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden
    sx={{
      ...frame(size),
      '@keyframes compass-settle': {
        '0%, 100%': { transform: 'rotate(-18deg)' },
        '45%': { transform: 'rotate(14deg)' },
        '70%': { transform: 'rotate(-5deg)' },
        '85%': { transform: 'rotate(2deg)' },
      },
      '& .compass-needle': {
        transformOrigin: '16px 16px',
        animation: 'compass-settle 5s cubic-bezier(.4,0,.2,1) infinite',
      },
      ...reduceMotion,
    }}
  >
    <circle cx="16" cy="16" r="12.5" stroke="currentColor" strokeWidth="2" opacity="0.35" />
    <g className="compass-needle">
      <path d="M16 7.5 L19 16 L16 24.5 L13 16 Z" fill="currentColor" />
    </g>
    <circle cx="16" cy="16" r="1.8" fill="currentColor" />
  </Box>
);

/** "I know roughly where I am" — three bars filling in turn, like levels. */
export const ClimbIcon = ({ size = 30 }) => (
  <Box
    component="svg"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden
    sx={{
      ...frame(size),
      '@keyframes climb-step': {
        '0%, 100%': { opacity: 0.28 },
        '50%': { opacity: 1 },
      },
      // Plain <rect> with a class, not <Box component="rect">: MUI's Box treats
      // `width` and `height` as style props, so they never reach the element as
      // SVG geometry attributes and the bars render with no size at all.
      '& .climb-bar': { animation: 'climb-step 2.4s ease-in-out infinite' },
      '& .climb-bar-2': { animationDelay: '.45s' },
      '& .climb-bar-3': { animationDelay: '.9s' },
      ...reduceMotion,
    }}
  >
    <rect className="climb-bar climb-bar-1" x="5" y="19" width="6" height="8" rx="2" fill="currentColor" />
    <rect className="climb-bar climb-bar-2" x="13" y="13" width="6" height="14" rx="2" fill="currentColor" />
    <rect className="climb-bar climb-bar-3" x="21" y="7" width="6" height="20" rx="2" fill="currentColor" />
  </Box>
);

/** "My exam is booked" — a second hand ticking, one step at a time. */
export const ClockIcon = ({ size = 30 }) => (
  <Box
    component="svg"
    viewBox="0 0 32 32"
    fill="none"
    aria-hidden
    sx={{
      ...frame(size),
      // `steps(12)` is what makes it tick rather than sweep — a smooth rotation
      // reads as a loading spinner, which is the wrong idea entirely.
      '@keyframes clock-tick': { to: { transform: 'rotate(360deg)' } },
      '& .clock-hand': { transformOrigin: '16px 16px', animation: 'clock-tick 12s steps(12) infinite' },
      ...reduceMotion,
    }}
  >
    <circle cx="16" cy="16" r="12.5" stroke="currentColor" strokeWidth="2" opacity="0.35" />
    <path d="M16 9.5 V16 L20 18.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
    <line
      className="clock-hand"
      x1="16"
      y1="16"
      x2="16"
      y2="8"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <circle cx="16" cy="16" r="1.6" fill="currentColor" />
  </Box>
);

const ICONS = { compass: CompassIcon, climb: ClimbIcon, clock: ClockIcon };

export const AnimatedIcon = ({ name, size }) => {
  const Icon = ICONS[name];
  return Icon ? <Icon size={size} /> : null;
};

export default AnimatedIcon;
